import React from "react";
import { SectionShell, Field, RadioGroup } from "./PartnerFormPrimitives";
import { PRICING_BASIS, TRADE_PRICING_FIELDS } from "./partnerConstants";
import { pricingKey } from "./partnerHelpers";

function TradePricingBlock({ trade, pricing, setPricing }) {
  const fields = TRADE_PRICING_FIELDS[trade] || [];
  if (fields.length === 0) {
    return (
      <div className="border border-dashed border-gray-300 rounded p-4">
        <div className="font-display tracking-widest uppercase text-sm text-br-bronze mb-1">{trade}</div>
        <div className="text-xs text-gray-500">No standard pricing units — please describe your pricing approach in the notes field below.</div>
      </div>
    );
  }
  return (
    <div className="border border-gray-200 rounded p-4 bg-br-paper/40" data-testid={`partner-pricing-block-${trade.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="font-display tracking-widest uppercase text-sm text-br-bronze mb-3">{trade}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map((f) => {
          const k = pricingKey(trade, f.key);
          return (
            <Field key={k} label={f.label} hint={f.hint}>
              <div className="relative">
                {f.prefix && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{f.prefix}</span>
                )}
                <input
                  className="input-br"
                  style={f.prefix ? { paddingLeft: 28 } : undefined}
                  value={pricing[k] || ""}
                  onChange={(e) => setPricing({ ...pricing, [k]: e.target.value })}
                  placeholder="0.00"
                  inputMode="decimal"
                />
              </div>
            </Field>
          );
        })}
      </div>
    </div>
  );
}

export default function PartnerPricingByTrade({ data, setRaw }) {
  const setPricing = (newPricing) => setRaw("trade_pricing", newPricing);
  const selected = (data.trades_offered || []).filter((t) => t !== "Other");

  return (
    <SectionShell
      index={4}
      title="Pricing & Labor Structure"
      subtitle="Trade-specific rates so we can match your bid to relevant scopes quickly."
      testId="partner-section-pricing"
    >
      <Field label="Pricing basis" required>
        <RadioGroup
          options={PRICING_BASIS}
          value={data.pricing_basis}
          onChange={(v) => setRaw("pricing_basis", v)}
          testIdPrefix="partner-pricing-basis"
        />
      </Field>

      {selected.length === 0 ? (
        <div className="text-sm text-gray-500 bg-br-paper/40 border border-dashed border-gray-300 rounded p-4" data-testid="partner-pricing-empty">
          Select trades in the previous section to see trade-specific pricing fields.
        </div>
      ) : (
        <div className="space-y-3" data-testid="partner-pricing-trade-blocks">
          {selected.map((trade) => (
            <TradePricingBlock
              key={trade}
              trade={trade}
              pricing={data.trade_pricing || {}}
              setPricing={setPricing}
            />
          ))}
        </div>
      )}

      <Field label="Pricing notes, minimums, and mobilization fees">
        <textarea
          rows={3}
          className="textarea-br"
          value={data.pricing_notes}
          onChange={(e) => setRaw("pricing_notes", e.target.value)}
          placeholder="e.g. $500 mobilization, 4-hour minimum on service calls..."
        />
      </Field>
    </SectionShell>
  );
}
